<template>
  <div>
    <v-row>
      <v-col
        cols="12"
        md="12"
      >
        <base-card>
          <v-card-title class="pb-0 production-card-title">
            <v-btn
              icon
              color="primary"
              class="mr-2 production-back-btn"
              @click="goToProductionList"
            >
              <v-icon>
                mdi-arrow-left
              </v-icon>
            </v-btn>

            <div>
              <div class="card-title mb-0">
                Production: {{ template.name }}
              </div>

              <div
                v-if="template.root && template.root.name"
                class="production-subtitle"
              >
                Root: {{ template.root.name }}
                {{ template.root.compW }}x{{ template.root.compH }},
                {{ Number(template.root.compDur || 0).toFixed(2) }} sec
                · {{ template.props.length }} layers
              </div>
            </div>

            <v-spacer />

            <v-btn
              text
              color="primary"
              class="mr-2"
              :disabled="!template.id"
              :to="{
                path: '/nexrender/',
                query: {
                  page: 1,
                  templates: template.id,
                },
              }"
            >
              <v-icon left>
                mdi-format-list-checks
              </v-icon>

              View Template Jobs
            </v-btn>

            <v-btn
              text
              color="primary"
              @click="goToTemplateDetails"
            >
              <v-icon left>
                mdi-file-document-outline
              </v-icon>

              Template Details
            </v-btn>
          </v-card-title>

          <v-card-text>
            <v-row class="mt-0 pt-0">
              <v-col
                cols="12"
                md="6"
                class="pb-0"
              >
                <div class="media-title">
                  Poster
                </div>

                <div class="media-preview-box">
                  <v-img
                    v-if="template.pictureURL"
                    :src="template.pictureURL"
                    contain
                    height="160"
                  />

                  <div
                    v-else
                    class="media-empty"
                  >
                    <v-icon
                      size="28"
                      color="grey"
                    >
                      mdi-image-outline
                    </v-icon>

                    <div class="mt-1">
                      No poster
                    </div>
                  </div>
                </div>

                <input
                  v-model.trim="template.pictureURL"
                  class="prop-input media-url-input mt-2"
                  placeholder="Poster URL"
                >

                <div class="d-flex align-center mt-1">
                  <v-file-input
                    v-model="template.production.posterFile"
                    hide-input
                    hide-details
                    prepend-icon="mdi-upload"
                    class="pa-0 ma-0 mr-2"
                    accept="image/*"
                    dense
                    :loading="posterUploading"
                    :disabled="posterUploading"
                    @change="uploadProductionPoster"
                  />

                  <span class="text--disabled text-caption">
                    {{ posterUploading ? 'uploading poster...' : 'upload poster' }}
                  </span>
                </div>
              </v-col>
              <v-col
                cols="12"
                md="6"
                class="pb-0"
              >
                <div class="media-title">
                  Preview Video
                </div>

                <div class="media-preview-box">
                  <video
                    v-if="template.videoURL"
                    :src="template.videoURL"
                    :poster="template.pictureURL"
                    controls
                    playsinline
                    class="media-video"
                  />

                  <div
                    v-else
                    class="media-empty"
                  >
                    <v-icon
                      size="28"
                      color="grey"
                    >
                      mdi-video-outline
                    </v-icon>

                    <div class="mt-1">
                      No video
                    </div>
                  </div>
                </div>

                <input
                  v-model.trim="template.videoURL"
                  class="prop-input media-url-input mt-2"
                  placeholder="Video URL"
                >

                <div class="d-flex align-center mt-1">
                  <v-file-input
                    v-model="template.production.videoFile"
                    hide-input
                    hide-details
                    prepend-icon="mdi-upload"
                    class="pa-0 ma-0 mr-2"
                    accept="video/*"
                    dense
                    :loading="videoUploading"
                    :disabled="videoUploading"
                    @change="uploadProductionVideo"
                  />

                  <span class="text--disabled text-caption">
                    {{ videoUploading ? 'uploading video...' : 'upload video' }}
                  </span>
                </div>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions>
            <v-spacer />

            <v-btn
              color="success"
              :loading="mediaLoading"
              @click="updateProductionMedia"
            >
              Update Media
            </v-btn>
          </v-card-actions>
        </base-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col
        cols="12"
        md="12"
      >
        <base-card>
          <v-card-title class="pb-0">
            <div class="card-title mb-0">
              Production Info
            </div>
          </v-card-title>

          <v-card-text>
            <v-row class="mt-0 pt-0">
              <v-col
                cols="12"
                md="8"
                class="pb-0"
              >
                <div class="production-info-label">
                  Title
                </div>

                <input
                  v-model.trim="template.productionTitle"
                  class="prop-input production-info-input"
                  placeholder="Title visible for user"
                >
              </v-col>

              <v-col
                cols="12"
                md="2"
                class="pb-0"
              >
                <div class="production-info-label">
                  Type
                </div>

                <select
                  v-model="template.productionType"
                  class="form-select production-info-select"
                  @change="productionTypeChanged"
                >
                  <option value="">
                    Select
                  </option>

                  <option
                    v-for="type in productionTypes"
                    :key="type.value"
                    :value="type.value"
                  >
                    {{ type.text }}
                  </option>
                </select>
              </v-col>

              <v-col
                cols="12"
                md="2"
                class="pb-0"
              >
                <div class="production-info-label">
                  Duration, sec
                </div>

                <input
                  v-model.number="template.duration"
                  type="number"
                  min="0"
                  step="0.01"
                  class="prop-input production-info-input"
                  placeholder="60.00"
                >
              </v-col>
            </v-row>

            <v-row class="mt-1 pt-0">
              <v-col
                cols="12"
                md="12"
                class="pb-0"
              >
                <div class="production-info-label">
                  Description
                </div>

                <textarea
                  v-model.trim="template.productionDescription"
                  class="prop-input production-info-textarea"
                  placeholder="Description visible for user"
                  rows="2"
                />
              </v-col>
            </v-row>

            <v-row class="mt-1 pt-0">
              <v-col
                cols="12"
                md="6"
                class="pb-0"
              >
                <div class="production-info-label">
                  Priority
                </div>

                <select
                  v-model.number="template.priority"
                  class="form-select production-info-select"
                >
                  <option
                    v-for="option in priorityOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.value }} — {{ option.text }}
                  </option>
                </select>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions>
            <v-spacer />

            <v-btn
              color="success"
              :loading="infoLoading"
              @click="updateProductionInfo"
            >
              Update Info
            </v-btn>
          </v-card-actions>
        </base-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col
        cols="12"
        md="12"
      >
        <base-card>
          <v-card-title class="pb-0 production-settings-title">
            <div>
              <div class="card-title mb-0">
                Production Settings
              </div>

              <div
                v-if="productionPresetApplied"
                class="production-preset-summary"
              >
                <span class="production-preset-summary--matched">
                  {{ productionPresetMatchedCount }} matched
                </span>

                <span class="production-preset-summary--missing">
                  {{ productionPresetMissingCount }} not found
                </span>
              </div>
            </div>

            <v-spacer />

            <v-btn
              small
              outlined
              color="primary"
              :loading="productionPresetLoading"
              :disabled="!template.props.length"
              @click="loadProductionLayerPreset"
            >
              <v-icon
                left
                small
              >
                mdi-database-arrow-down-outline
              </v-icon>

              Load from Preset
            </v-btn>
          </v-card-title>

          <v-card-text>
            <v-row class="production-header">
              <v-col
                cols="12"
                md="2"
                class="pb-1"
              >
                Layer
              </v-col>

              <v-col
                cols="12"
                md="1"
                class="pb-1"
              >
                Type
              </v-col>

              <v-col
                cols="12"
                md="3"
                class="pb-1"
              >
                Label for user
              </v-col>

              <v-col
                cols="12"
                md="4"
                class="pb-1"
              >
                Mode
              </v-col>

              <v-col
                cols="12"
                md="2"
                class="pb-1"
              >
                Backend Dynamic Field
              </v-col>
            </v-row>

            <v-row
              v-for="(prop, i) in template.props"
              :key="prop.layer + i"
              class="production-row mt-0 pt-0"
              :class="{
                'production-row--preset': prop.productionPresetState === 'matched',
                'production-row--missing': prop.productionPresetState === 'missing',
              }"
            >
              <v-col
                cols="12"
                md="2"
                class="py-0"
              >
                <input
                  :value="prop.layer"
                  class="prop-input production-input production-locked"
                  disabled
                >
              </v-col>
              <v-col
                cols="12"
                md="1"
                class="py-0"
              >
                <input
                  :value="prop.type"
                  class="prop-input production-type production-locked"
                  disabled
                >
              </v-col>
              <v-col
                cols="12"
                md="3"
                class="py-0"
              >
                <input
                  v-model.trim="prop.productionLabel"
                  class="prop-input production-input"
                  placeholder="Label"
                >
              </v-col>
              <v-col
                cols="12"
                md="4"
                class="py-0"
              >
                <div class="production-radio-line">
                  <label>
                    <input
                      v-model="prop.productionMode"
                      type="radio"
                      value="auto_manual"
                    >
                    Auto / Manual
                  </label>

                  <label>
                    <input
                      v-model="prop.productionMode"
                      type="radio"
                      value="only_auto"
                    >
                    Only Auto
                  </label>

                  <label>
                    <input
                      v-model="prop.productionMode"
                      type="radio"
                      value="only_manual"
                    >
                    Only Manual
                  </label>
                </div>
              </v-col>
              <v-col
                cols="12"
                md="2"
                class="py-0"
              >
                <select
                  v-if="hasAutoMode(prop)"
                  v-model="prop.productionAutoSource"
                  class="form-select production-select"
                >
                  <option value="">
                    Select
                  </option>

                  <option
                    v-for="option in backendDynamicFieldOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.text }}
                  </option>
                </select>

                <span
                  v-else
                  class="production-manual-only"
                >
                  manual only
                </span>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions>
            <v-spacer />

            <v-btn
              color="success"
              :loading="settingsLoading"
              @click="updateProductionSettings"
            >
              Update Settings
            </v-btn>
          </v-card-actions>
        </base-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col
        cols="12"
        md="12"
      >
        <base-card :data-loading="templateChannelsLoading">
          <v-card-title class="pb-0 channels-card-title">
            <div class="card-title mb-0">
              Channels
            </div>

            <v-spacer />

            <v-btn
              color="primary"
              :disabled="!availableChannels.length"
              @click="openAddChannelDialog"
            >
              <v-icon left>
                mdi-plus
              </v-icon>
              Add to Channel
            </v-btn>
          </v-card-title>

          <v-card-text>
            <v-simple-table
              v-if="productionChannelRows.length"
              class="channels-table"
            >
              <template v-slot:default>
                <thead>
                <tr>
                  <th class="text-left">
                    Channel
                  </th>

                  <th class="text-left">
                    Template Type
                  </th>

                  <th class="text-left">
                    Status
                  </th>

                  <th class="text-left">
                    Selected Preview
                  </th>

                  <th class="text-left">
                    Settings
                  </th>

                  <th class="text-right">
                    Actions
                  </th>
                </tr>
                </thead>

                <tbody>
                <tr
                  v-for="row in productionChannelRows"
                  :key="row.id"
                  :class="{ 'channel-row--active': row.active }"
                >
                  <td>
                    <div
                      class="channel-cell"
                      :class="{ 'channel-cell--suspended': row.suspended }"
                    >
                      <div class="channel-icon-col">
                        <v-icon
                          v-if="row.suspended"
                          small
                          color="warning"
                          class="channel-pause-icon"
                        >
                          mdi-pause
                        </v-icon>
                      </div>

                      <div class="channel-text-col">
                        <div class="channel-name">
                          {{ row.channelName }}
                        </div>

                        <div class="channel-id">
                          id={{ row.channelId }}
                        </div>
                      </div>
                    </div>
                  </td>

                  <td>
                    {{ formatTemplateType(row.type) }}
                  </td>

                  <td>
                    <div class="channel-status-cell">
                      <v-chip
                        v-if="row.active"
                        small
                        color="success lighten-5"
                        text-color="success darken-1"
                        class="channel-active-chip"
                      >
                        <v-icon
                          left
                          x-small
                          color="success"
                        >
                          mdi-check-circle
                        </v-icon>

                        Active
                      </v-chip>

                      <v-chip
                        v-else
                        small
                        color="grey lighten-3"
                        text-color="grey darken-1"
                      >
                        <v-icon
                          left
                          x-small
                        >
                          mdi-circle-outline
                        </v-icon>

                        {{ row.selectedOnly ? 'Inactive' : (row.previewResource ? 'Inactive' : 'Not configured') }}
                      </v-chip>

                      <div
                        v-if="row.selected"
                        class="channel-selected-label"
                      >
                        SELECTED
                      </div>

                      <div
                        v-if="row.previewResource && !row.previewUrl"
                        class="channel-preview-missing-label"
                      >
                        selected resource has no preview URL
                      </div>
                    </div>
                  </td>

                  <td>
                    <button
                      v-if="row.previewUrl"
                      type="button"
                      class="channel-preview-card"
                      :class="{ 'channel-preview-card--selected': row.selected }"
                      :aria-label="`Open preview for ${row.channelName}`"
                      @click.stop="openChannelPreviewDialog(row)"
                    >
                      <div class="channel-preview-card-icon">
                        <v-icon
                          color="white"
                          size="34"
                        >
                          mdi-play
                        </v-icon>
                      </div>

                      <div class="channel-preview-card-text">
                        <div class="channel-preview-card-title">
                          Preview
                        </div>

                        <div class="channel-preview-card-meta">
                          {{ Number(row.previewDuration || 0).toFixed(2) }} sec
                        </div>
                      </div>
                    </button>

                    <div
                      v-else
                      class="channel-preview-empty"
                    >
                      <v-icon
                        small
                        color="grey lighten-1"
                      >
                        mdi-video-off-outline
                      </v-icon>

                      <span>
                        {{ row.isOverlay ? 'No preview required' : 'No selected preview' }}
                      </span>
                    </div>
                  </td>

                  <td>
                    <v-btn
                      small
                      text
                      color="primary"
                      @click="openSettingsDialog(row)"
                    >
                      <v-icon
                        left
                        small
                      >
                        mdi-code-json
                      </v-icon>
                      View
                    </v-btn>
                  </td>

                  <td class="text-right">
                    <v-btn
                      small
                      text
                      color="primary"
                      class="mr-1"
                      @click="openMoveToDialog(row)"
                    >
                      <v-icon
                        left
                        small
                      >
                        mdi-swap-horizontal
                      </v-icon>
                      Copy & Assign To
                    </v-btn>

                    <v-btn
                      icon
                      small
                      color="error"
                      @click="openRemoveChannelTemplateDialog(row)"
                    >
                      <v-icon small>
                        mdi-trash-can-outline
                      </v-icon>
                    </v-btn>
                  </td>
                </tr>
                </tbody>
              </template>
            </v-simple-table>

            <div
              v-else
              class="channels-empty"
            >
              <v-icon
                size="32"
                color="grey"
              >
                mdi-television-off
              </v-icon>

              <div class="mt-2">
                This template is not assigned to any channel yet.
              </div>
            </div>
          </v-card-text>
        </base-card>
      </v-col>
    </v-row>

    <v-dialog
      v-model="channelPreviewDialog"
      max-width="1000"
      @input="onChannelPreviewDialogInput"
    >
      <v-card>
        <v-card-title class="channel-preview-dialog-title">
          <div>
            <div class="text-h5">
              Channel Preview
            </div>

            <div
              v-if="channelPreviewDialogRow"
              class="channel-preview-dialog-subtitle"
            >
              {{ channelPreviewDialogRow.channelName }}
              · {{ formatTemplateType(channelPreviewDialogRow.type) }}
              · {{ Number(channelPreviewDialogRow.previewDuration || 0).toFixed(2) }} sec
            </div>
          </div>

          <v-spacer />

          <v-btn
            icon
            @click="closeChannelPreviewDialog"
          >
            <v-icon>
              mdi-close
            </v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="channel-preview-dialog-body">
          <div class="channel-preview-dialog-player">
            <video
              ref="channelPreviewDialogVideo"
              class="channel-preview-dialog-video"
              controls
              playsinline
              preload="metadata"
              @play="channelPreviewDialogPlaying = true"
              @pause="channelPreviewDialogPlaying = false"
              @ended="channelPreviewDialogPlaying = false"
            />

            <div
              v-if="channelPreviewDialogLoading"
              class="channel-preview-dialog-overlay"
            >
              <v-progress-circular
                indeterminate
                color="white"
                size="48"
              />
            </div>

            <button
              v-else-if="!channelPreviewDialogPlaying && !channelPreviewDialogError"
              type="button"
              class="channel-preview-dialog-play"
              @click.stop="startChannelPreviewPlayback"
            >
              <v-icon
                color="white"
                size="58"
              >
                mdi-play
              </v-icon>
            </button>

            <div
              v-if="channelPreviewDialogError"
              class="channel-preview-dialog-error"
            >
              <v-icon
                color="error"
                size="32"
              >
                mdi-alert-circle-outline
              </v-icon>

              <div>
                {{ channelPreviewDialogError }}
              </div>
            </div>
          </div>

          <div
            v-if="channelPreviewDialogRow"
            class="channel-preview-dialog-url"
          >
            {{ channelPreviewDialogRow.previewUrl }}
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            text
            @click="closeChannelPreviewDialog"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="addChannelDialog"
      max-width="520"
    >
      <v-card>
        <v-card-title class="text-h5">
          Add Template to Channel
        </v-card-title>

        <v-card-text>
          <v-label class="font-weight-medium mb-2">
            Channel
          </v-label>

          <v-select
            v-model="selectedChannelId"
            :items="availableChannelOptions"
            item-text="text"
            item-value="value"
            outlined
            dense
            hide-details
            placeholder="Select channel"
          />

          <div class="text--disabled text-caption mt-2">
            The template will be assigned to the selected channel
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            text
            @click="addChannelDialog = false"
          >
            Cancel
          </v-btn>

          <v-btn
            color="primary"
            :loading="addChannelLoading"
            :disabled="!selectedChannelId"
            @click="addTemplateToChannel"
          >
            Add
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="removeChannelTemplateDialog"
      max-width="520"
    >
      <v-card>
        <v-card-title class="text-h5">
          Remove Template from Channel
        </v-card-title>

        <v-card-text>
          Are you sure you want to remove this template from
          <strong>{{ removedChannelTemplate ? removedChannelTemplate.channelName : '' }}</strong>?
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            text
            @click="removeChannelTemplateDialog = false"
          >
            Cancel
          </v-btn>

          <v-btn
            color="error"
            :loading="removeChannelTemplateLoading"
            @click="removeTemplateFromChannel"
          >
            Remove
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="settingsDialog"
      max-width="980"
    >
      <v-card>
        <v-card-title class="settings-dialog-title">
          Channel Template Settings
        </v-card-title>

        <v-card-text>
          <div
            v-if="selectedSettingsRow"
            class="settings-dialog-subtitle"
          >
            {{ selectedSettingsRow.channelName }}
          </div>

          <v-simple-table
            v-if="selectedSettingsProps.length"
            dense
            class="settings-props-table"
          >
            <template v-slot:default>
              <thead>
              <tr>
                <th class="text-left">
                  Layer
                </th>

                <th class="text-left">
                  Type
                </th>

                <th class="text-left">
                  Auto
                </th>

                <th class="text-left">
                  Value
                </th>
              </tr>
              </thead>

              <tbody>
              <tr
                v-for="(prop, index) in selectedSettingsProps"
                :key="prop.layer + index"
              >
                <td class="settings-layer">
                  {{ prop.layer }}
                </td>

                <td class="settings-type">
                  {{ prop.type }}
                </td>

                <td class="settings-auto">
                  <v-chip
                    x-small
                    :color="prop.auto ? 'primary lighten-5' : 'grey lighten-3'"
                    :text-color="prop.auto ? 'primary' : 'grey darken-1'"
                  >
                    {{ prop.auto ? 'auto' : 'manual' }}
                  </v-chip>
                </td>

                <td class="settings-value">
                  <a
                    v-if="isSettingsMediaProp(prop)"
                    :href="getSettingsEffectiveValue(prop)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="settings-media-link"
                    @click.stop
                  >
                    <div class="settings-media-box">
                      <img
                        v-if="isSettingsImageProp(prop)"
                        :src="getSettingsEffectiveValue(prop)"
                        class="settings-media-img"
                        alt=""
                      >

                      <video
                        v-if="isSettingsVideoProp(prop)"
                        :src="getSettingsEffectiveValue(prop)"
                        class="settings-media-video"
                        muted
                        playsinline
                        preload="metadata"
                      />

                      <div class="settings-media-open">
                        <v-icon
                          small
                          color="white"
                        >
                          mdi-open-in-new
                        </v-icon>
                      </div>
                    </div>

                    <div class="settings-media-url">
                      {{ getSettingsEffectiveValue(prop) }}
                    </div>
                  </a>

                  <span
                    v-else
                    :class="{ 'settings-auto-value': prop.auto && getSettingsEffectiveValue(prop) }"
                  >
                      {{ getSettingsValueText(prop) }}
                    </span>
                </td>
              </tr>
              </tbody>
            </template>
          </v-simple-table>

          <div
            v-else
            class="text--disabled text-center pa-6"
          >
            No settings props
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            text
            @click="settingsDialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="moveToDialog"
      max-width="1500"
      scrollable
    >
      <v-card>
        <v-card-title class="move-dialog-title">
          <div>
            <div class="settings-dialog-title pa-0">
              Copy & Assign Channel Template
            </div>

            <div
              v-if="moveToRow"
              class="move-dialog-subtitle"
            >
              {{ moveToRow.channelName }}
              · {{ formatTemplateType(moveToRow.type) }}
            </div>
          </div>

          <v-spacer />

          <v-btn
            icon
            @click="closeMoveToDialog"
          >
            <v-icon>
              mdi-close
            </v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="move-dialog-body">
          <v-row class="move-template-selector-row">
            <v-col
              cols="12"
              md="5"
            >
              <div class="move-template-label">
                Current Template
              </div>

              <div class="move-template-card">
                <div class="move-template-card-title">
                  {{ template.productionTitle || 'No production title' }}
                </div>

                <div class="move-template-card-name">
                  {{ template.name || `Template #${template.id}` }}
                </div>

                <div class="move-template-card-meta">
                  ID {{ template.id }}
                  · {{ formatTemplateType(moveToRow ? moveToRow.type : '') }}
                </div>
              </div>
            </v-col>

            <v-col
              cols="12"
              md="2"
              class="move-template-arrow-col"
            >
              <v-icon
                size="34"
                color="primary"
              >
                mdi-arrow-right
              </v-icon>
            </v-col>

            <v-col
              cols="12"
              md="5"
            >
              <div class="move-template-label">
                Copy & Assign To
              </div>

              <v-select
                v-model="moveTargetTemplateId"
                :items="moveTargetTemplateOptions"
                item-text="text"
                item-value="value"
                :loading="moveTargetTemplatesLoading"
                :disabled="moveTargetTemplatesLoading"
                outlined
                dense
                hide-details
                placeholder="Select production template"
                no-data-text="No other production templates"
                @change="selectMoveTargetTemplate"
              >
                <template v-slot:item="{ item, attrs, on }">
                  <v-list-item
                    v-bind="attrs"
                    v-on="on"
                    :class="{
                      'move-template-option--same-type': item.sameType,
                    }"
                  >
                    <v-list-item-content>
                      <v-list-item-title class="d-flex align-center">
                        <v-chip
                          v-if="item.sameType"
                          x-small
                          color="primary"
                          text-color="white"
                          class="mr-2"
                        >
                          SAME TYPE
                        </v-chip>

                        <span>
                          {{ item.text }}
                        </span>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-select>

              <div
                v-if="moveTargetTemplate"
                class="move-template-card mt-3"
              >
                <div class="move-template-card-title">
                  {{ moveTargetTemplate.productionTitle || 'No production title' }}
                </div>

                <div class="move-template-card-name">
                  {{ moveTargetTemplate.name || `Template #${moveTargetTemplate.id}` }}
                </div>

                <div class="move-template-card-meta">
                  ID {{ moveTargetTemplate.id }}
                  · {{ formatTemplateType(moveTargetTemplate.productionType) }}
                </div>
              </div>
            </v-col>
          </v-row>

          <div
            v-if="moveTargetTemplate"
            class="move-comparison"
          >
            <v-simple-table
              dense
              class="settings-props-table move-comparison-table"
            >
              <template v-slot:default>
                <thead>
                <tr>
                  <th
                    colspan="4"
                    class="move-group-header move-group-header--current"
                  >
                    Current Settings
                  </th>

                  <th class="move-arrow-header" />

                  <th
                    colspan="4"
                    class="move-group-header move-group-header--new"
                  >
                    New Template
                  </th>
                </tr>

                <tr>
                  <th class="text-left">
                    Layer
                  </th>
                  <th class="text-left">
                    Type
                  </th>
                  <th class="text-left">
                    Auto
                  </th>
                  <th class="text-left">
                    Value
                  </th>

                  <th class="move-arrow-header" />

                  <th class="text-left">
                    Layer
                  </th>
                  <th class="text-left">
                    Type
                  </th>
                  <th class="text-left">
                    Auto
                  </th>
                  <th class="text-left">
                    Value
                  </th>
                </tr>
                </thead>

                <tbody>
                <tr
                  v-for="(row, index) in moveComparisonRows"
                  :key="row.layer + index"
                  :class="{
                      'move-row--matched': row.matched,
                      'move-row--new': !row.source && row.target,
                      'move-row--removed': row.source && !row.target,
                    }"
                >
                  <td class="settings-layer move-layer-cell">
                      <span v-if="row.source">
                        {{ row.source.layer }}
                      </span>

                    <span
                      v-else
                      class="move-empty-value"
                    >
                        —
                      </span>
                  </td>

                  <td class="settings-type">
                      <span v-if="row.source">
                        {{ row.source.type }}
                      </span>

                    <span
                      v-else
                      class="move-empty-value"
                    >
                        —
                      </span>
                  </td>

                  <td class="settings-auto">
                    <v-chip
                      v-if="row.source"
                      x-small
                      :color="row.source.auto ? 'primary lighten-5' : 'grey lighten-3'"
                      :text-color="row.source.auto ? 'primary' : 'grey darken-1'"
                    >
                      {{ row.source.auto ? 'auto' : 'manual' }}
                    </v-chip>

                    <span
                      v-else
                      class="move-empty-value"
                    >
                        —
                      </span>
                  </td>

                  <td class="settings-value move-value-cell">
                    <template v-if="row.source">
                      <a
                        v-if="isMoveMediaProp(row.source)"
                        :href="getMoveEffectiveValue(row.source)"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="settings-media-link"
                        @click.stop
                      >
                        <div class="settings-media-box move-media-box">
                          <img
                            v-if="isMoveImageProp(row.source)"
                            :src="getMoveEffectiveValue(row.source)"
                            class="settings-media-img"
                            alt=""
                          >

                          <video
                            v-if="isMoveVideoProp(row.source)"
                            :src="getMoveEffectiveValue(row.source)"
                            class="settings-media-video"
                            muted
                            playsinline
                            preload="metadata"
                          />

                          <div class="settings-media-open">
                            <v-icon
                              small
                              color="white"
                            >
                              mdi-open-in-new
                            </v-icon>
                          </div>
                        </div>

                        <div class="settings-media-url">
                          {{ getMoveEffectiveValue(row.source) }}
                        </div>
                      </a>

                      <span
                        v-else
                        :class="{ 'settings-auto-value': row.source.auto && getMoveEffectiveValue(row.source) }"
                      >
                          {{ getMoveValueText(row.source) }}
                        </span>
                    </template>

                    <span
                      v-else
                      class="move-empty-value"
                    >
                        —
                      </span>
                  </td>

                  <td class="move-arrow-cell">
                    <v-icon
                      v-if="row.matched"
                      small
                      color="primary"
                    >
                      mdi-arrow-right
                    </v-icon>

                    <v-icon
                      v-else-if="row.source && !row.target"
                      small
                      color="grey"
                    >
                      mdi-minus
                    </v-icon>

                    <v-icon
                      v-else
                      small
                      color="primary"
                    >
                      mdi-plus
                    </v-icon>
                  </td>

                  <td class="settings-layer move-layer-cell">
                      <span v-if="row.target">
                        {{ row.target.layer }}
                      </span>

                    <span
                      v-else
                      class="move-empty-value"
                    >
                        —
                      </span>
                  </td>

                  <td class="settings-type">
                      <span v-if="row.target">
                        {{ row.target.type }}
                      </span>

                    <span
                      v-else
                      class="move-empty-value"
                    >
                        —
                      </span>
                  </td>

                  <td class="settings-auto">
                    <v-chip
                      v-if="row.target"
                      x-small
                      :color="row.target.auto ? 'primary lighten-5' : 'grey lighten-3'"
                      :text-color="row.target.auto ? 'primary' : 'grey darken-1'"
                    >
                      {{ row.target.auto ? 'auto' : 'manual' }}
                    </v-chip>

                    <span
                      v-else
                      class="move-empty-value"
                    >
                        —
                      </span>
                  </td>

                  <td class="settings-value move-value-cell">
                    <template v-if="row.target">
                      <a
                        v-if="isMoveMediaProp(row.target)"
                        :href="getMoveEffectiveValue(row.target)"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="settings-media-link"
                        @click.stop
                      >
                        <div class="settings-media-box move-media-box">
                          <img
                            v-if="isMoveImageProp(row.target)"
                            :src="getMoveEffectiveValue(row.target)"
                            class="settings-media-img"
                            alt=""
                          >

                          <video
                            v-if="isMoveVideoProp(row.target)"
                            :src="getMoveEffectiveValue(row.target)"
                            class="settings-media-video"
                            muted
                            playsinline
                            preload="metadata"
                          />

                          <div class="settings-media-open">
                            <v-icon
                              small
                              color="white"
                            >
                              mdi-open-in-new
                            </v-icon>
                          </div>
                        </div>

                        <div class="settings-media-url">
                          {{ getMoveEffectiveValue(row.target) }}
                        </div>
                      </a>

                      <span
                        v-else
                        :class="{ 'settings-auto-value': row.target.auto && getMoveEffectiveValue(row.target) }"
                      >
                          {{ getMoveValueText(row.target) }}
                        </span>
                    </template>

                    <span
                      v-else
                      class="move-empty-value"
                    >
                        —
                      </span>
                  </td>
                </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>

          <div
            v-else-if="!moveTargetTemplatesLoading"
            class="move-target-empty"
          >
            Select a destination template to compare settings.
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            color="primary"
            :loading="moveToLoading"
            :disabled="!moveTargetTemplate || moveToLoading"
            @click="copyAndAssignChannelTemplate"
          >
            <v-icon left>
              mdi-content-copy
            </v-icon>

            Copy & Assign New
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Hls from 'hls.js'
import { aepProjectToJsonAPI, processAEPProject } from '@/store/converters'

export default {
  name: 'ProductionTemplate',

  metaInfo: {
    title: 'Production Template',
  },

  data () {
    return {
      mediaLoading: false,
      infoLoading: false,
      settingsLoading: false,

      productionPresetLoading: false,
      productionPresetApplied: false,
      productionPresetMatchedCount: 0,
      productionPresetMissingCount: 0,

      posterUploading: false,
      videoUploading: false,

      template: {
        id: null,
        name: '',
        description: '',

        root: {
          name: '',
          compW: 0,
          compH: 0,
          compDur: 0,
        },

        props: [],

        production: {
          posterFile: null,
          videoFile: null,
        },
      },

      productionTypes: [
        {
          value: 'intro',
          text: 'Channel Intro / Ident',
        },
        {
          value: 'announce',
          text: 'What\'s Next Announce',
        },
        {
          value: 'bumper',
          text: 'Bumper',
        },
        {
          value: 'overlay',
          text: 'Overlay',
        },
        {
          value: 'logo-reveal',
          text: 'Logo Reveal',
        },
        {
          value: 'blackout',
          text: 'Blackout / No Signal',
        },
        {
          value: 'live-start',
          text: 'Live Program Starting',
        },
        {
          value: 'live-end',
          text: 'Live Program Ended',
        },
        {
          value: 'channel-pitch',
          text: 'Channel Presentation / Pitch',
        },
        {
          value: 'ad-placeholder',
          text: 'Ad Placeholder',
        },
      ],

      priorityOptions: [
        {
          value: 0,
          text: 'Unclassified / lowest — test jobs',
        },
        {
          value: 2,
          text: 'Job without time constraint — logo reveal, blackout, one-off jobs',
        },
        {
          value: 4,
          text: 'Standard time constraint job — program announces',
        },
        {
          value: 6,
          text: 'High priority job — overlays',
        },
        {
          value: 8,
          text: 'Immediate / top priority — reserved',
        },
      ],

      backendDynamicFieldOptions: [
        {
          text: 'Program 1 — Title',
          value: 'prog1name',
        },
        {
          text: 'Program 1 — Start Time',
          value: 'prog1time',
        },
        {
          text: 'Program 1 — Image',
          value: 'prog1img',
        },
        {
          text: 'Program 1 — Description',
          value: 'prog1descr',
        },
        {
          text: 'Program 1 — Video',
          value: 'prog1video',
        },

        {
          text: 'Program 2 — Title',
          value: 'prog2name',
        },
        {
          text: 'Program 2 — Start Time',
          value: 'prog2time',
        },
        {
          text: 'Program 2 — Image',
          value: 'prog2img',
        },
        {
          text: 'Program 2 — Description',
          value: 'prog2descr',
        },
        {
          text: 'Program 2 — Video',
          value: 'prog2video',
        },

        {
          text: 'Program 3 — Title',
          value: 'prog3name',
        },
        {
          text: 'Program 3 — Start Time',
          value: 'prog3time',
        },
        {
          text: 'Program 3 — Image',
          value: 'prog3img',
        },
        {
          text: 'Program 3 — Description',
          value: 'prog3descr',
        },
        {
          text: 'Program 3 — Video',
          value: 'prog3video',
        },

        {
          text: 'Channel — Logo',
          value: 'channelLogo',
        },
        {
          text: 'Channel — Short Description',
          value: 'channelTagline',
        },
        {
          text: 'Channel — Copyright',
          value: 'channelCopyright',
        },
        {
          text: 'Channel — Contact Information',
          value: 'channelContact',
        },
        {
          text: 'Channel — Primary Color',
          value: 'channelPrimaryColor',
        },
        {
          text: 'Channel — Secondary Color',
          value: 'channelSecondaryColor',
        },
        {
          text: 'Channel — Heading Font',
          value: 'channelHeadingFont',
        },
        {
          text: 'Channel — Body Font',
          value: 'channelBodyFont',
        },
        {
          text: 'Channel — Music',
          value: 'channelMusic',
        },
        {
          text: 'NP Logo — 1',
          value: 'npLogo1',
        },
        {
          text: 'NP Logo — 0',
          value: 'npLogo0',
        },
        {
          text: 'Rating — Badge Image',
          value: 'ratingImage',
        },
        {
          text: 'Rating — Label',
          value: 'ratingText',
        },
        {
          text: 'Rating — Advisories',
          value: 'ratingAdv',
        },

        {
          text: 'Channel Social — Website',
          value: 'channelSocialWebsite',
        },
        {
          text: 'Channel Social — Instagram',
          value: 'channelSocialInstagram',
        },
        {
          text: 'Channel Social — X / Twitter',
          value: 'channelSocialTwitter',
        },
        {
          text: 'Channel Social — Facebook',
          value: 'channelSocialFacebook',
        },
        {
          text: 'Channel Social — Snapchat',
          value: 'channelSocialSnapchat',
        },
        {
          text: 'Channel Social — YouTube',
          value: 'channelSocialYoutube',
        },
        {
          text: 'Channel Social — Telegram',
          value: 'channelSocialTelegram',
        },
        {
          text: 'Channel Social — Tumblr',
          value: 'channelSocialTumblr',
        },
        {
          text: 'Channel Social — Discord',
          value: 'channelSocialDiscord',
        },
        {
          text: 'Channel Social — BeReal',
          value: 'channelSocialBereal',
        },
        {
          text: 'Channel Social — Spotify',
          value: 'channelSocialSpotify',
        },
      ],

      channels: [],
      channelTemplates: [],
      channelTemplateResourcesByTemplateId: {},

      channelPreviewDialog: false,
      channelPreviewDialogRow: null,
      channelPreviewDialogHls: null,
      channelPreviewDialogLoading: false,
      channelPreviewDialogPlaying: false,
      channelPreviewDialogError: '',

      templateChannelsLoading: false,

      addChannelDialog: false,
      addChannelLoading: false,
      selectedChannelId: null,

      removeChannelTemplateDialog: false,
      removeChannelTemplateLoading: false,
      removedChannelTemplate: null,

      settingsDialog: false,
      selectedSettingsRow: null,

      moveToDialog: false,
      moveToRow: null,
      moveToLoading: false,
      moveTargetTemplatesLoading: false,
      moveTargetTemplates: [],
      moveTargetTemplateId: null,
      moveTargetTemplate: null,
    }
  },

  computed: {
    channelById () {
      const map = {}

      for (let i = 0; i < this.channels.length; i += 1) {
        map[this.channels[i].id] = this.channels[i]
      }

      return map
    },

    productionChannelRows () {
      return this.channelTemplates.map((item) => {
        const channelId = this.getChannelTemplateChannelId(item)
        const channel = this.channelById[channelId] || {}

        const previewResource =
          this.channelTemplateResourcesByTemplateId[item.id] ||
          null

        const previewUrl =
          previewResource &&
          previewResource.url
            ? previewResource.url
            : ''

        const type =
          String(item.type || '')
            .trim()
            .toLowerCase()

        const isAnnounce =
          type === 'announce'

        const isOverlay =
          type === 'overlay'

        const selectedOnly =
          isAnnounce ||
          isOverlay

        // Announce and Overlay are special:
        // channel_template.selected alone controls whether they are active.
        //
        // Overlay never requires a preview resource.
        //
        // All other production template types are active only when
        // there is a selected channel-template-resource with a preview URL.
        const selected =
          selectedOnly
            ? !!item.selected
            : !!(
              previewResource &&
              previewResource.selected
            )

        const active =
          selectedOnly
            ? !!item.selected
            : selected && !!previewUrl

        return {
          id: item.id,
          raw: item,
          channelId,
          channelName: channel.name || `Channel #${channelId}`,
          suspended: !!channel.suspended,
          type: item.type || '',

          isAnnounce,
          isOverlay,
          selectedOnly,

          previewResource,
          previewUrl,
          previewDuration:
            previewResource
              ? Number(previewResource.duration || 0)
              : 0,

          selected,
          active,
        }
      })
    },

    availableChannels () {
      const used = {}

      for (let i = 0; i < this.channelTemplates.length; i += 1) {
        const channelId = this.getChannelTemplateChannelId(
          this.channelTemplates[i],
        )

        if (channelId) {
          used[channelId] = true
        }
      }

      return this.channels.filter(
        (channel) => !used[channel.id],
      )
    },

    availableChannelOptions () {
      return this.availableChannels.map((channel) => ({
        text: channel.name,
        value: channel.id,
      }))
    },

    selectedSettingsProps () {
      if (!this.selectedSettingsRow) {
        return []
      }

      const settings = this.getChannelTemplateSettings(
        this.selectedSettingsRow.raw,
      )

      if (
        !settings ||
        !Array.isArray(settings.aep_project_props)
      ) {
        return []
      }

      return settings.aep_project_props
    },

    selectedSettingsDuration () {
      if (!this.selectedSettingsRow) {
        return 0
      }

      const settings = this.getChannelTemplateSettings(
        this.selectedSettingsRow.raw,
      )

      return Number(settings.duration || 0)
    },

    moveTargetTemplateOptions () {
      const sourceType =
        this.moveToRow
          ? String(this.moveToRow.type || '')
          : ''

      return this.moveTargetTemplates
        .map((item) => {
          const productionTitle =
            item.productionTitle ||
            'No production title'

          const name =
            item.name ||
            `Template #${item.id}`

          const sameType =
            String(item.productionType || '') ===
            sourceType

          return {
            text:
              `${this.formatTemplateType(item.productionType)} · ` +
              `${productionTitle} — ${name}`,
            value: item.id,
            sameType,
            productionType:
              item.productionType || '',
            productionTitle,
            name,
          }
        })
        .sort((a, b) => {
          if (a.sameType !== b.sameType) {
            return a.sameType ? -1 : 1
          }

          const typeCompare =
            String(a.productionType)
              .localeCompare(
                String(b.productionType),
              )

          if (typeCompare !== 0) {
            return typeCompare
          }

          const titleCompare =
            String(a.productionTitle)
              .localeCompare(
                String(b.productionTitle),
              )

          if (titleCompare !== 0) {
            return titleCompare
          }

          return String(a.name)
            .localeCompare(String(b.name))
        })
    },

    moveSourceSettingsProps () {
      if (!this.moveToRow) {
        return []
      }

      const settings = this.getChannelTemplateSettings(
        this.moveToRow.raw,
      )

      if (
        !settings ||
        !Array.isArray(settings.aep_project_props)
      ) {
        return []
      }

      return settings.aep_project_props
    },

    moveTargetSettingsProps () {
      if (!this.moveTargetTemplate) {
        return []
      }

      return this.buildMoveTargetSettings(
        this.moveTargetTemplate,
      )
    },

    moveComparisonRows () {
      const sourceProps =
        this.moveSourceSettingsProps
      const targetProps =
        this.moveTargetSettingsProps

      const targetByLayer = {}
      const usedTargetLayers = {}

      for (
        let i = 0;
        i < targetProps.length;
        i += 1
      ) {
        targetByLayer[targetProps[i].layer] =
          targetProps[i]
      }

      const rows = []

      for (
        let i = 0;
        i < sourceProps.length;
        i += 1
      ) {
        const source = sourceProps[i]
        const target =
          targetByLayer[source.layer] || null

        if (target) {
          usedTargetLayers[target.layer] = true
        }

        rows.push({
          layer: source.layer,
          source,
          target,
          matched: !!target,
        })
      }

      for (
        let i = 0;
        i < targetProps.length;
        i += 1
      ) {
        const target = targetProps[i]

        if (usedTargetLayers[target.layer]) {
          continue
        }

        rows.push({
          layer: target.layer,
          source: null,
          target,
          matched: false,
        })
      }

      return rows
    },
  },

  created () {
    this.getTemplate(
      this.$route.params.template_id,
    )
  },

  beforeDestroy () {
    this.destroyChannelPreviewDialogPlayer()
  },

  methods: {
    getTemplate (id) {
      this.$store.commit('setPageLoader')

      const args = {
        'id[eq]': id,
      }

      this.$store.dispatch('aepProjects', args)
        .then((res) => {
          const items = res || []

          if (!items.length) {
            this.$router.push({
              name: 'ProductionPage',
            })

            return
          }

          const template =
            processAEPProject(items[0])

          this.template = template

          this.ensureProductionFields()
          this.loadTemplateChannels(template.id)
        })
        .catch((errorMsg) => {
          this.$store.commit('showSnackbar', {
            text: errorMsg,
            timeout: 6000,
            color: 'error',
          })

          this.$router.push({
            name: 'ProductionPage',
          })
        })
        .finally(() => {
          this.$store.commit(
            'resolvePageLoader',
          )
        })
    },

    ensureProductionFields () {
      if (!this.template.production) {
        this.$set(
          this.template,
          'production',
          {
            posterFile: null,
            videoFile: null,
          },
        )
      }

      this.$set(
        this.template.production,
        'posterFile',
        null,
      )

      this.$set(
        this.template.production,
        'videoFile',
        null,
      )

      if (!this.template.duration) {
        this.$set(
          this.template,
          'duration',
          this.template.root &&
          this.template.root.compDur
            ? Number(
              this.template.root.compDur,
            )
            : 0,
        )
      }

      if (
        typeof this.template.priority ===
        'undefined' ||
        this.template.priority === null
      ) {
        this.$set(
          this.template,
          'priority',
          0,
        )
      }

      if (!Array.isArray(this.template.props)) {
        this.$set(
          this.template,
          'props',
          [],
        )
      }

      for (
        let i = 0;
        i < this.template.props.length;
        i += 1
      ) {
        const prop = this.template.props[i]

        if (
          typeof prop.productionLabel ===
          'undefined'
        ) {
          this.$set(
            prop,
            'productionLabel',
            prop.displayName ||
            prop.layer ||
            '',
          )
        }

        if (
          typeof prop.productionMode ===
          'undefined'
        ) {
          this.$set(
            prop,
            'productionMode',
            'auto_manual',
          )
        }

        if (
          typeof prop.productionAutoSource ===
          'undefined'
        ) {
          this.$set(
            prop,
            'productionAutoSource',
            '',
          )
        }

        if (
          typeof prop.productionPresetState ===
          'undefined'
        ) {
          this.$set(
            prop,
            'productionPresetState',
            '',
          )
        }

        if (
          typeof prop.productionPreset ===
          'undefined'
        ) {
          this.$set(
            prop,
            'productionPreset',
            null,
          )
        }
      }
    },

    productionTypeChanged () {
      this.productionPresetApplied = false
      this.productionPresetMatchedCount = 0
      this.productionPresetMissingCount = 0

      for (
        let i = 0;
        i < this.template.props.length;
        i += 1
      ) {
        const prop = this.template.props[i]

        this.$set(
          prop,
          'productionPreset',
          null,
        )

        this.$set(
          prop,
          'productionPresetState',
          '',
        )
      }
    },

    getProductionPresetResultRows (result) {
      if (Array.isArray(result)) {
        return result
      }

      if (
        result &&
        Array.isArray(result.items)
      ) {
        return result.items
      }

      return []
    },

    getProductionPresetLayers (
      settings,
      type,
    ) {
      if (
        settings &&
        settings.types &&
        typeof settings.types === 'object'
      ) {
        const typePreset =
          settings.types[type]

        if (Array.isArray(typePreset)) {
          return typePreset
        }

        if (
          typePreset &&
          Array.isArray(typePreset.layers)
        ) {
          return typePreset.layers
        }

        return []
      }

      if (Array.isArray(settings)) {
        return settings
      }

      if (
        settings &&
        Array.isArray(settings.layers)
      ) {
        return settings.layers
      }

      return []
    },

    normalizeProductionPresetLayerName (value) {
      return String(value || '')
        .trim()
        .toUpperCase()
    },

    parseProductionLayerPreset (value) {
      if (!value) {
        return {
          version: 1,
          layers: [],
        }
      }

      if (typeof value === 'object') {
        return value
      }

      try {
        return JSON.parse(value)
      } catch (error) {
        throw new Error(
          'Invalid Production Layer Preset JSON',
        )
      }
    },

    markProductionPresetMissing () {
      for (
        let i = 0;
        i < this.template.props.length;
        i += 1
      ) {
        const prop = this.template.props[i]

        this.$set(
          prop,
          'productionPreset',
          null,
        )

        this.$set(
          prop,
          'productionPresetState',
          'missing',
        )
      }

      this.productionPresetApplied = true
      this.productionPresetMatchedCount = 0

      this.productionPresetMissingCount =
        this.template.props.length
    },

    applyProductionLayerPreset (layers) {
      const presetMap = {}

      for (
        let i = 0;
        i < layers.length;
        i += 1
      ) {
        const preset = layers[i]

        const layer =
          this.normalizeProductionPresetLayerName(
            preset && preset.layer,
          )

        if (layer) {
          presetMap[layer] = preset
        }
      }

      let matchedCount = 0
      let missingCount = 0

      for (
        let i = 0;
        i < this.template.props.length;
        i += 1
      ) {
        const prop = this.template.props[i]

        const layer =
          this.normalizeProductionPresetLayerName(
            prop.layer,
          )

        const preset = presetMap[layer]

        if (!preset) {
          this.$set(
            prop,
            'productionPreset',
            null,
          )

          this.$set(
            prop,
            'productionPresetState',
            'missing',
          )

          missingCount += 1

          continue
        }

        const mode = [
          'auto_manual',
          'only_auto',
          'only_manual',
        ].includes(preset.mode)
          ? preset.mode
          : 'auto_manual'

        const autoSource =
          preset.auto_source ||
          preset.autoSource ||
          ''

        this.$set(
          prop,
          'productionPreset',
          preset,
        )

        this.$set(
          prop,
          'productionPresetState',
          'matched',
        )

        this.$set(
          prop,
          'productionLabel',
          typeof preset.label === 'string'
            ? preset.label
            : prop.productionLabel ||
            prop.displayName ||
            prop.layer ||
            '',
        )

        this.$set(
          prop,
          'productionMode',
          mode,
        )

        this.$set(
          prop,
          'productionAutoSource',
          mode === 'only_manual'
            ? ''
            : autoSource,
        )

        matchedCount += 1
      }

      this.productionPresetApplied = true

      this.productionPresetMatchedCount =
        matchedCount

      this.productionPresetMissingCount =
        missingCount
    },

    loadProductionLayerPreset () {
      if (this.productionPresetLoading) {
        return
      }

      if (!this.template.productionType) {
        window.alert(
          'Please select the template type first',
        )

        return
      }

      this.productionPresetLoading = true

      const params = {
        'id[eq]': 1,
      }

      this.$store.dispatch(
        'playoutSettings',
        params,
      )
        .then((result) => {
          const rows =
            this.getProductionPresetResultRows(
              result,
            )

          if (!rows.length) {
            this.markProductionPresetMissing()

            this.$store.commit(
              'showSnackbar',
              {
                text:
                  'Global production layer preset was not found',
                timeout: 6000,
                color: 'error',
              },
            )

            return
          }

          const settings =
            this.parseProductionLayerPreset(
              rows[0].aep_layer_preset,
            )

          const layers =
            this.getProductionPresetLayers(
              settings,
              this.template.productionType,
            )

          this.applyProductionLayerPreset(
            layers,
          )

          this.$store.commit(
            'showSnackbar',
            {
              text:
                `Preset loaded for ${this.getProductionTypeLabel(this.template.productionType)}: ` +
                `${this.productionPresetMatchedCount} matched, ` +
                `${this.productionPresetMissingCount} not found. ` +
                'Click Update Settings to save.',
              timeout: 6000,
              color:
                this.productionPresetMissingCount
                  ? 'warning'
                  : 'success',
            },
          )
        })
        .catch((errorMsg) => {
          this.$store.commit(
            'showSnackbar',
            {
              text:
                errorMsg &&
                errorMsg.message
                  ? errorMsg.message
                  : errorMsg,
              timeout: 6000,
              color: 'error',
            },
          )
        })
        .finally(() => {
          this.productionPresetLoading = false
        })
    },

    getProductionTypeLabel (type) {
      const option =
        this.productionTypes.find(
          (item) =>
            item.value === type,
        )

      return option
        ? option.text
        : type
    },

    getChannelTemplateChannelId (item) {
      return item
        ? item.channel_id
        : 0
    },

    formatTemplateType (type) {
      return this.getProductionTypeLabel(type) || '—'
    },

    hasAutoMode (prop) {
      return (
        prop.productionMode ===
        'auto_manual' ||
        prop.productionMode ===
        'only_auto'
      )
    },

    uploadFile (fileObj) {
      const file = new FormData()

      file.append(
        'uploadfile',
        fileObj,
        fileObj.name,
      )

      const params = {
        file,
        name: fileObj.name,
      }

      return this.$store.dispatch(
        'ftpUpload',
        params,
      )
        .then((result) => result.url)
    },

    updateProductionMedia () {
      this.mediaLoading = true

      this.$store.commit(
        'setPageLoader',
      )

      this.saveTemplate()
        .then(() => {
          this.$store.commit(
            'showSnackbar',
            {
              text:
                'Production media has been updated',
              timeout: 3000,
              color: 'success',
            },
          )
        })
        .catch((errorMsg) => {
          this.$store.commit(
            'showSnackbar',
            {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            },
          )
        })
        .finally(() => {
          this.mediaLoading = false

          this.$store.commit(
            'resolvePageLoader',
          )
        })
    },

    updateProductionInfo () {
      this.infoLoading = true

      this.$store.commit(
        'setPageLoader',
      )

      this.saveTemplate()
        .then(() => {
          this.$store.commit(
            'showSnackbar',
            {
              text:
                'Production info has been updated',
              timeout: 3000,
              color: 'success',
            },
          )
        })
        .catch((errorMsg) => {
          this.$store.commit(
            'showSnackbar',
            {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            },
          )
        })
        .finally(() => {
          this.infoLoading = false

          this.$store.commit(
            'resolvePageLoader',
          )
        })
    },

    updateProductionSettings () {
      this.settingsLoading = true

      this.$store.commit(
        'setPageLoader',
      )

      for (
        let i = 0;
        i < this.template.props.length;
        i += 1
      ) {
        const prop = this.template.props[i]

        if (
          prop.productionMode ===
          'only_manual'
        ) {
          prop.productionAutoSource = ''
        }
      }

      this.saveTemplate()
        .then(() => {
          this.$store.commit(
            'showSnackbar',
            {
              text:
                'Production settings have been updated',
              timeout: 3000,
              color: 'success',
            },
          )
        })
        .catch((errorMsg) => {
          this.$store.commit(
            'showSnackbar',
            {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            },
          )
        })
        .finally(() => {
          this.settingsLoading = false

          this.$store.commit(
            'resolvePageLoader',
          )
        })
    },

    saveTemplate () {
      const updated =
        aepProjectToJsonAPI(
          this.template,
        )

      return this.$store.dispatch(
        'patchAEPProject',
        updated,
      )
    },

    goToTemplateDetails () {
      if (!this.template.id) {
        return
      }

      this.$router.push({
        name: 'TemplateDetails',

        params: {
          template_id: this.template.id,
        },
      })
    },

    goToProductionList () {
      this.$router.push({
        name: 'ProductionPage',
      })
    },

    uploadProductionPoster (fileObj) {
      if (
        !fileObj ||
        this.posterUploading
      ) {
        return
      }

      this.posterUploading = true

      this.uploadFile(fileObj)
        .then((url) => {
          this.template.pictureURL = url

          this.template.production.posterFile =
            null

          this.$store.commit(
            'showSnackbar',
            {
              text:
                'Poster has been uploaded',
              timeout: 3000,
              color: 'success',
            },
          )
        })
        .catch((errorMsg) => {
          this.template.posterFile = null

          this.$store.commit(
            'showSnackbar',
            {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            },
          )
        })
        .finally(() => {
          this.posterUploading = false
        })
    },

    uploadProductionVideo (fileObj) {
      if (
        !fileObj ||
        this.videoUploading
      ) {
        return
      }

      this.videoUploading = true

      this.uploadFile(fileObj)
        .then((url) => {
          this.template.videoURL = url

          this.template.production.videoFile =
            null

          this.$store.commit(
            'showSnackbar',
            {
              text:
                'Video has been uploaded',
              timeout: 3000,
              color: 'success',
            },
          )
        })
        .catch((errorMsg) => {
          this.template.production.videoFile =
            null

          this.$store.commit(
            'showSnackbar',
            {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            },
          )
        })
        .finally(() => {
          this.videoUploading = false
        })
    },

    loadTemplateChannels (templateId) {
      if (!templateId) {
        return
      }

      this.templateChannelsLoading = true
      this.closeChannelPreviewDialog()

      const channelTemplateParams = {
        'aep.id[eq]': templateId,
      }

      const channelParams = {
        $orderby: 'name',
      }

      Promise.all([
        this.$store.dispatch(
          'channels',
          channelParams,
        ),

        this.$store.dispatch(
          'channelTemplates',
          channelTemplateParams,
        ),
      ])
        .then((res) => {
          this.channels = res[0] || []

          const channelTemplates =
            res[1] || []

          this.channelTemplates =
            channelTemplates

          return Promise.all(
            channelTemplates.map(
              (channelTemplate) =>
                this.loadChannelTemplatePreviewResource(
                  channelTemplate,
                ),
            ),
          )
        })
        .then((resources) => {
          const map = {}

          for (
            let i = 0;
            i < this.channelTemplates.length;
            i += 1
          ) {
            const channelTemplate =
              this.channelTemplates[i]

            map[channelTemplate.id] =
              resources[i] || null
          }

          this.channelTemplateResourcesByTemplateId =
            map
        })
        .catch((errorMsg) => {
          this.$store.commit(
            'showSnackbar',
            {
              text:
                errorMsg &&
                errorMsg.message
                  ? errorMsg.message
                  : errorMsg,
              timeout: 6000,
              color: 'error',
            },
          )
        })
        .finally(() => {
          this.templateChannelsLoading =
            false
        })
    },

    loadChannelTemplatePreviewResource (
      channelTemplate,
    ) {
      if (
        !channelTemplate ||
        !channelTemplate.id
      ) {
        return Promise.resolve(null)
      }

      const type =
        String(
          channelTemplate.type || '',
        )
          .trim()
          .toLowerCase()

      const isAnnounce =
        type === 'announce'

      const isOverlay =
        type === 'overlay'

      // Overlay does not have previews/resources by design.
      if (isOverlay) {
        return Promise.resolve(null)
      }

      const params = {
        'template.id[eq]':
        channelTemplate.id,
        $limit: 1,
        $orderby: '-id',
      }

      // Announce is special:
      // its activity comes from channel_template.selected,
      // so preview lookup must NOT filter resource.selected.
      if (!isAnnounce) {
        params['selected[eq]'] = 1
      }

      return this.$store.dispatch(
        'channelTemplateResources',
        params,
      )
        .then((resources) => {
          const list =
            Array.isArray(resources)
              ? resources
              : []

          // We intentionally request only one row ordered by newest id.
          return list.length
            ? list[0]
            : null
        })
    },

    openChannelPreviewDialog (row) {
      if (
        !row ||
        !row.previewUrl
      ) {
        return
      }

      this.destroyChannelPreviewDialogPlayer()

      this.channelPreviewDialogRow =
        row

      this.channelPreviewDialogError =
        ''

      this.channelPreviewDialogPlaying =
        false

      this.channelPreviewDialog =
        true

      this.$nextTick(() => {
        this.initChannelPreviewDialogPlayer()
      })
    },

    onChannelPreviewDialogInput (value) {
      if (!value) {
        this.closeChannelPreviewDialog()
      }
    },

    closeChannelPreviewDialog () {
      this.destroyChannelPreviewDialogPlayer()

      this.channelPreviewDialog =
        false

      this.channelPreviewDialogRow =
        null

      this.channelPreviewDialogLoading =
        false

      this.channelPreviewDialogPlaying =
        false

      this.channelPreviewDialogError =
        ''
    },

    getChannelPreviewDialogVideo () {
      const ref =
        this.$refs.channelPreviewDialogVideo

      return Array.isArray(ref)
        ? ref[0]
        : ref
    },

    destroyChannelPreviewDialogPlayer () {
      const video =
        this.getChannelPreviewDialogVideo()

      if (video) {
        try {
          video.pause()
        } catch (e) {}

        try {
          video.removeAttribute('src')
          video.load()
        } catch (e) {}
      }

      if (
        this.channelPreviewDialogHls &&
        typeof this.channelPreviewDialogHls.destroy ===
        'function'
      ) {
        this.channelPreviewDialogHls.destroy()
      }

      this.channelPreviewDialogHls =
        null
    },

    initChannelPreviewDialogPlayer () {
      const row =
        this.channelPreviewDialogRow

      const video =
        this.getChannelPreviewDialogVideo()

      if (
        !row ||
        !row.previewUrl ||
        !video
      ) {
        return
      }

      this.channelPreviewDialogLoading =
        true

      this.channelPreviewDialogError =
        ''

      // IMPORTANT:
      // Prefer hls.js first. Modern Chrome may report native HLS
      // support via canPlayType("application/vnd.apple.mpegurl")
      // but still fail to play the stream.
      if (Hls.isSupported()) {
        const hls = new Hls()

        this.channelPreviewDialogHls =
          hls

        hls.loadSource(
          row.previewUrl,
        )

        hls.attachMedia(video)

        hls.on(
          Hls.Events.MANIFEST_PARSED,
          () => {
            this.channelPreviewDialogLoading =
              false
          },
        )

        hls.on(
          Hls.Events.ERROR,
          (event, data) => {
            if (!data || !data.fatal) {
              return
            }

            this.channelPreviewDialogLoading =
              false

            this.channelPreviewDialogPlaying =
              false

            this.channelPreviewDialogError =
              data.details
                ? `Preview error: ${data.details}`
                : 'Unable to load preview'
          },
        )

        return
      }

      // Native HLS fallback, mainly for Safari.
      if (
        video.canPlayType(
          'application/vnd.apple.mpegurl',
        )
      ) {
        video.src =
          row.previewUrl

        const onReady = () => {
          this.channelPreviewDialogLoading =
            false

          video.removeEventListener(
            'loadedmetadata',
            onReady,
          )
        }

        const onError = () => {
          this.channelPreviewDialogLoading =
            false

          this.channelPreviewDialogError =
            'Unable to load preview'

          video.removeEventListener(
            'error',
            onError,
          )
        }

        video.addEventListener(
          'loadedmetadata',
          onReady,
        )

        video.addEventListener(
          'error',
          onError,
        )

        video.load()

        return
      }

      this.channelPreviewDialogLoading =
        false

      this.channelPreviewDialogError =
        'HLS playback is not supported by this browser'
    },

    startChannelPreviewPlayback () {
      const video =
        this.getChannelPreviewDialogVideo()

      if (!video) {
        return
      }

      const playPromise =
        video.play()

      if (
        playPromise &&
        typeof playPromise.catch ===
        'function'
      ) {
        playPromise.catch((error) => {
          this.channelPreviewDialogPlaying =
            false

          this.channelPreviewDialogError =
            error && error.message
              ? error.message
              : 'Unable to start preview'
        })
      }
    },

    openAddChannelDialog () {
      this.selectedChannelId =
        this.availableChannels.length
          ? this.availableChannels[0].id
          : null

      this.addChannelDialog = true
    },

    getAepProjectPropSettingsType (prop) {
      if (prop.type === 'Image') {
        return 'image'
      }

      if (prop.type === 'Video') {
        return 'video'
      }

      if (prop.type === 'Media') {
        return 'video'
      }

      if (prop.type === 'Audio') {
        return 'audio'
      }

      return 'data'
    },

    buildChannelTemplateSettings () {
      const props = []

      for (
        let i = 0;
        i < this.template.props.length;
        i += 1
      ) {
        const prop = this.template.props[i]

        const auto =
          prop.productionMode !==
          'only_manual'

        let value =
          prop.defaultValue || ''

        if (
          auto &&
          prop.productionAutoSource
        ) {
          value =
            prop.productionAutoSource
        }

        props.push({
          layer: prop.layer,
          value,

          type:
            this.getAepProjectPropSettingsType(
              prop,
            ),

          auto,
        })
      }

      return {
        duration: Number(
          this.template.duration || 0,
        ),

        aep_project_props: props,
      }
    },

    addTemplateToChannel () {
      if (
        !this.template.id ||
        !this.selectedChannelId
      ) {
        return
      }

      this.addChannelLoading = true

      const payload = {
        template_id: 1,

        channel_id: Number(
          this.selectedChannelId,
        ),

        aep_project_id: Number(
          this.template.id,
        ),

        type:
          this.template.productionType ||
          '',

        active: true,
        state: 'done',

        settings:
          this.buildChannelTemplateSettings(),
      }

      this.$store.dispatch(
        'createChannelTemplate',
        payload,
      )
        .then(() => {
          this.$store.commit(
            'showSnackbar',
            {
              text:
                'Template has been added to channel',
              timeout: 3000,
              color: 'success',
            },
          )

          this.addChannelDialog = false
          this.selectedChannelId = null

          this.loadTemplateChannels(
            this.template.id,
          )
        })
        .catch((errorMsg) => {
          this.$store.commit(
            'showSnackbar',
            {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            },
          )
        })
        .finally(() => {
          this.addChannelLoading = false
        })
    },

    openRemoveChannelTemplateDialog (row) {
      this.removedChannelTemplate = row

      this.removeChannelTemplateDialog =
        true
    },

    removeTemplateFromChannel () {
      if (
        !this.removedChannelTemplate ||
        !this.removedChannelTemplate.id
      ) {
        return
      }

      this.removeChannelTemplateLoading =
        true

      this.$store.dispatch(
        'deleteChannelTemplate',
        this.removedChannelTemplate,
      )
        .then(() => {
          this.$store.commit(
            'showSnackbar',
            {
              text:
                'Template has been removed from channel',
              timeout: 3000,
              color: 'success',
            },
          )

          this.removeChannelTemplateDialog =
            false

          this.removedChannelTemplate =
            null

          this.loadTemplateChannels(
            this.template.id,
          )
        })
        .catch((errorMsg) => {
          this.$store.commit(
            'showSnackbar',
            {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            },
          )
        })
        .finally(() => {
          this.removeChannelTemplateLoading =
            false
        })
    },

    openMoveToDialog (row) {
      this.moveToRow = row
      this.moveTargetTemplateId = null
      this.moveTargetTemplate = null
      this.moveTargetTemplates = []
      this.moveToDialog = true

      this.loadMoveTargetTemplates(row)
    },

    closeMoveToDialog () {
      this.moveToDialog = false
      this.moveToRow = null
      this.moveTargetTemplateId = null
      this.moveTargetTemplate = null
      this.moveTargetTemplates = []
    },

    loadMoveTargetTemplates (row) {
      if (!row) {
        return
      }

      this.moveTargetTemplatesLoading = true

      const params = {
        'is_production[eq]': 1,
        'is_active[eq]': 1,
        $orderby: 'prod_type,prod_title,name',
      }

      this.$store.dispatch(
        'aepProjects',
        params,
      )
        .then((res) => {
          const items = Array.isArray(res)
            ? res
            : []

          this.moveTargetTemplates =
            items
              .map((raw) => ({
                raw,
                template: processAEPProject(raw),
              }))
              .filter((entry) => {
                const item = entry.template
                const raw = entry.raw || {}

                if (!item || !item.id) {
                  return false
                }

                if (
                  Number(item.id) ===
                  Number(this.template.id)
                ) {
                  return false
                }

                const isProduction =
                  Number(
                    typeof raw.is_production !== 'undefined'
                      ? raw.is_production
                      : item.isProduction,
                  ) === 1

                return isProduction
              })
              .map((entry) => entry.template)
        })
        .catch((errorMsg) => {
          this.$store.commit(
            'showSnackbar',
            {
              text:
                errorMsg &&
                errorMsg.message
                  ? errorMsg.message
                  : errorMsg,
              timeout: 6000,
              color: 'error',
            },
          )
        })
        .finally(() => {
          this.moveTargetTemplatesLoading =
            false
        })
    },

    selectMoveTargetTemplate (templateId) {
      const id = Number(templateId)

      this.moveTargetTemplate =
        this.moveTargetTemplates.find(
          (item) =>
            Number(item.id) === id,
        ) || null
    },

    buildMoveTargetSettings (targetTemplate) {
      if (
        !targetTemplate ||
        !Array.isArray(targetTemplate.props)
      ) {
        return []
      }

      const sourceByLayer = {}

      for (
        let i = 0;
        i < this.moveSourceSettingsProps.length;
        i += 1
      ) {
        const source =
          this.moveSourceSettingsProps[i]

        sourceByLayer[source.layer] =
          source
      }

      const result = []

      for (
        let i = 0;
        i < targetTemplate.props.length;
        i += 1
      ) {
        const prop =
          targetTemplate.props[i]

        const targetType =
          this.getAepProjectPropSettingsType(
            prop,
          )

        const targetAuto =
          prop.productionMode !==
          'only_manual'

        let targetValue =
          prop.defaultValue || ''

        if (
          targetAuto &&
          prop.productionAutoSource
        ) {
          targetValue =
            prop.productionAutoSource
        }

        const source =
          sourceByLayer[prop.layer]

        if (source) {
          result.push({
            layer: prop.layer,
            type: targetType,
            auto: !!source.auto,
            value:
              typeof source.value ===
              'undefined'
                ? ''
                : source.value,
            transferred: true,
          })

          continue
        }

        result.push({
          layer: prop.layer,
          type: targetType,
          auto: targetAuto,
          value: targetValue,
          transferred: false,
        })
      }

      return result
    },

    buildMoveDefaultSettings (targetTemplate) {
      if (
        !targetTemplate ||
        !Array.isArray(targetTemplate.props)
      ) {
        return {
          duration: 0,
          aep_project_props: [],
        }
      }

      const props = []

      for (
        let i = 0;
        i < targetTemplate.props.length;
        i += 1
      ) {
        const prop = targetTemplate.props[i]
        const auto =
          prop.productionMode !==
          'only_manual'

        let value =
          prop.defaultValue || ''

        if (
          auto &&
          prop.productionAutoSource
        ) {
          value =
            prop.productionAutoSource
        }

        props.push({
          layer: prop.layer,
          value,
          type:
            this.getAepProjectPropSettingsType(
              prop,
            ),
          auto,
        })
      }

      return {
        duration: Number(
          targetTemplate.duration ||
          (
            targetTemplate.root &&
            targetTemplate.root.compDur
          ) ||
          0,
        ),
        aep_project_props: props,
      }
    },

    buildMoveChannelTemplatePayload () {
      if (
        !this.moveToRow ||
        !this.moveTargetTemplate
      ) {
        return null
      }

      const source =
        this.moveToRow.raw || {}

      const target =
        this.moveTargetTemplate

      const payload = {
        // Preserve the channel/template assignment metadata.
        template_id: Number(
          source.template_id ||
          1,
        ),

        picture_id: Number(
          source.picture_id ||
          0,
        ),

        rep_interval: Number(
          source.rep_interval ||
          0,
        ),

        type:
          target.productionType ||
          '',

        name: source.name || '',
        description:
          source.description || '',

        // Keep the same behavior as Add to Channel.
        // We intentionally do not reuse the old render job.
        state: 'done',

        default: !!source.default,
        selected: !!source.selected,

        active:
          typeof source.active ===
          'undefined'
            ? true
            : !!source.active,

        // Preserve channel playback settings exactly as configured.
        // Support both raw API snake_case and converter camelCase.
        playback_settings:
          typeof source.playback_settings !==
          'undefined'
            ? source.playback_settings
            : source.playbackSettings,

        // Main migrated user settings.
        settings: {
          duration: Number(
            target.duration ||
            (
              target.root &&
              target.root.compDur
            ) ||
            0,
          ),

          aep_project_props:
          this.moveTargetSettingsProps,
        },

        // Defaults must belong to the NEW AEP,
        // not to the old layer set.
        default_settings:
          this.buildMoveDefaultSettings(
            target,
          ),

        aep_project_id: Number(
          target.id,
        ),

        channel_id: Number(
          this.getChannelTemplateChannelId(
            source,
          ),
        ),
      }

      // Preserve user-facing optional fields when present.
      // These are safe metadata; render outputs are not copied.
      if (source.placeholder_url) {
        payload.placeholder_url =
          source.placeholder_url
      }

      return payload
    },

    copyAndAssignChannelTemplate () {
      if (
        this.moveToLoading ||
        !this.moveToRow ||
        !this.moveToRow.id ||
        !this.moveTargetTemplate
      ) {
        return
      }

      const payload =
        this.buildMoveChannelTemplatePayload()

      if (!payload) {
        return
      }

      this.moveToLoading = true

      // COPY & ASSIGN:
      // the existing channel_template is intentionally left untouched.
      // We only create a new assignment for the same channel using
      // the selected AEP project and migrated layer settings.
      this.$store.dispatch(
        'createChannelTemplate',
        payload,
      )
        .then(() => {
          this.$store.commit(
            'showSnackbar',
            {
              text: 'New template has been copied and assigned to the channel',
              timeout: 3000,
              color: 'success',
            },
          )

          this.closeMoveToDialog()

          // Reload the current template assignments.
          // The old assignment remains here by design; the newly created
          // assignment belongs to the selected target AEP project.
          this.loadTemplateChannels(
            this.template.id,
          )
        })
        .catch((errorMsg) => {
          this.$store.commit(
            'showSnackbar',
            {
              text:
                errorMsg &&
                errorMsg.message
                  ? errorMsg.message
                  : errorMsg,
              timeout: 6000,
              color: 'error',
            },
          )
        })
        .finally(() => {
          this.moveToLoading = false
        })
    },

    getMoveEffectiveValue (prop) {
      if (!prop) {
        return ''
      }

      return prop.value || ''
    },

    isMoveImageProp (prop) {
      return (
        String(prop && prop.type || '')
          .toLowerCase() === 'image' &&
        this.isHttpValue(
          this.getMoveEffectiveValue(prop),
        )
      )
    },

    isMoveVideoProp (prop) {
      return (
        String(prop && prop.type || '')
          .toLowerCase() === 'video' &&
        this.isHttpValue(
          this.getMoveEffectiveValue(prop),
        )
      )
    },

    isMoveMediaProp (prop) {
      return (
        this.isMoveImageProp(prop) ||
        this.isMoveVideoProp(prop)
      )
    },

    getMoveValueText (prop) {
      const value =
        this.getMoveEffectiveValue(prop)

      if (!value) {
        return '—'
      }

      if (prop.auto) {
        return this.getAutoSourceLabel(
          value,
        )
      }

      return value
    },

    getChannelTemplateSettings (item) {
      if (!item || !item.settings) {
        return {}
      }

      if (
        typeof item.settings ===
        'string'
      ) {
        try {
          return JSON.parse(
            item.settings,
          )
        } catch (err) {
          return {}
        }
      }

      return item.settings
    },

    openSettingsDialog (row) {
      this.selectedSettingsRow = row
      this.settingsDialog = true
    },

    getTemplatePropByLayer (layer) {
      if (
        !this.template ||
        !Array.isArray(
          this.template.props,
        )
      ) {
        return null
      }

      return (
        this.template.props.find(
          (prop) =>
            prop.layer === layer,
        ) || null
      )
    },

    getSettingsEffectiveValue (prop) {
      if (!prop) {
        return ''
      }

      if (!prop.auto) {
        return prop.value || ''
      }

      const templateProp =
        this.getTemplatePropByLayer(
          prop.layer,
        )

      if (
        templateProp &&
        templateProp.productionAutoSource
      ) {
        return (
          templateProp.productionAutoSource
        )
      }

      return prop.value || ''
    },

    isHttpValue (value) {
      return /^https?:\/\//i.test(
        String(value || '').trim(),
      )
    },

    isSettingsImageProp (prop) {
      const value =
        this.getSettingsEffectiveValue(
          prop,
        )

      return (
        String(prop.type || '')
          .toLowerCase() === 'image' &&
        this.isHttpValue(value)
      )
    },

    isSettingsVideoProp (prop) {
      const value =
        this.getSettingsEffectiveValue(
          prop,
        )

      return (
        String(prop.type || '')
          .toLowerCase() === 'video' &&
        this.isHttpValue(value)
      )
    },

    isSettingsMediaProp (prop) {
      return (
        this.isSettingsImageProp(prop) ||
        this.isSettingsVideoProp(prop)
      )
    },

    getAutoSourceLabel (value) {
      const option =
        this.backendDynamicFieldOptions.find(
          (item) =>
            item.value === value,
        )

      return option
        ? option.text
        : value
    },

    getSettingsValueText (prop) {
      const value =
        this.getSettingsEffectiveValue(
          prop,
        )

      if (!value) {
        return '—'
      }

      if (prop.auto) {
        return this.getAutoSourceLabel(
          value,
        )
      }

      return value
    },
  },
}
</script>

<style lang="scss" scoped>
.production-subtitle {
  font-size: 14px;
  color: #8a8a8a;
  font-weight: 400;
}

.card-title {
  line-height: 18px;
}

.media-title {
  margin-bottom: 6px;
  color: grey;
  font-size: 16px;
}

.media-preview-box {
  height: 160px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: #f8f9fa;
  overflow: hidden;
}

.media-empty {
  width: 100%;
  height: 100%;
  color: #9aa4b2;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.media-video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
}

.media-url-input {
  width: 100%;
}

.production-info-label {
  margin-bottom: 3px;
  color: grey;
  font-size: 14px;
  line-height: 18px;
  font-weight: 400;
}

.production-info-input {
  width: 100%;
  height: 31px;
  font-size: 14px;
  line-height: 20px;
}

.production-info-textarea {
  width: 100%;
  min-height: 50px;
  max-height: 90px;
  resize: vertical;
  font-size: 14px;
  line-height: 20px;
}

.production-settings-title {
  align-items: flex-start;
}

.production-preset-summary {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
}

.production-preset-summary--matched {
  color: #1976d2;
}

.production-preset-summary--missing {
  color: #d64545;
}

.production-header {
  color: grey;
  font-size: 16px;
}

.production-row {
  padding-bottom: 4px;
  transition: background-color 0.15s ease;
}

.production-row--preset {
  background: #eef7ff;
  box-shadow: inset 3px 0 0 #90caf9;
}

.production-row--missing {
  background: #fff1f1;
  box-shadow: inset 3px 0 0 #ef9a9a;
}

.production-input {
  width: 100%;
}

.production-type {
  width: 100%;
  min-width: 70px;
}

.production-locked {
  background: #f1f3f5;
  color: #2c3e50;
}

.production-radio-line {
  height: 31px;
  display: flex;
  align-items: center;
  gap: 14px;
  white-space: nowrap;
}

.production-radio-line label {
  display: flex;
  align-items: center;
  gap: 5px;
  margin: 0;
  color: #2c3e50;
  font-size: 14px;
  cursor: pointer;
}

.production-radio-line input {
  margin: 0;
}

.production-select {
  width: 100%;
  height: 31px;
}

.production-manual-only {
  height: 31px;
  display: flex;
  align-items: center;
  color: #8a8a8a;
  font-size: 14px;
}

.prop-input {
  padding: 2px 5px;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
}

.form-select {
  cursor: pointer;
  display: block;
  width: 100%;
  padding: 2px 5px;
  -moz-padding-start: calc(0.75rem - 3px);
  font-weight: 400;
  color: #212529;
  background-color: #e9ecef;

  background-image:
    url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e");

  background-repeat: no-repeat;
  background-position: right 0.25rem center;
  background-size: 16px 12px;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;

  transition:
    border-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out;

  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

.production-card-title {
  align-items: flex-start;
}

.production-back-btn {
  margin-top: 2px;
}

.channels-card-title {
  align-items: center;
}

.channels-table th {
  color: grey;
  font-size: 14px;
  font-weight: 500;
}

.channels-table td {
  vertical-align: middle;
}

.channel-name {
  font-size: 15px;
  font-weight: 600;
  color: #2c3e50;
}

.channel-id {
  margin-top: 1px;
  font-size: 12px;
  color: #8a8a8a;
}

::v-deep .channels-table tbody tr.channel-row--active {
  background: #f4fbf6;
}

::v-deep .channels-table tbody tr.channel-row--active:hover {
  background: #eef8f1 !important;
}

.channel-status-cell {
  min-width: 150px;
}

.channel-active-chip {
  font-weight: 700;
}

.channel-selected-label {
  margin-top: 4px;
  color: #2e7d32;
  font-size: 10px;
  line-height: 13px;
  font-weight: 800;
  letter-spacing: 0.08em;
}

.channel-preview-missing-label {
  max-width: 170px;
  margin-top: 4px;
  color: #9a6700;
  font-size: 11px;
  line-height: 14px;
}

.channel-preview-card {
  width: 190px;
  height: 62px;
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 8px 12px;
  border: 1px solid #dfe3ea;
  border-radius: 8px;
  outline: none;
  background: #f7f9fc;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.channel-preview-card:hover {
  border-color: var(--v-primary-base);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
}

.channel-preview-card--selected {
  border-color: #81c784;
  background: #f4fbf6;
}

.channel-preview-card-icon {
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--v-primary-base);
}

.channel-preview-card-icon .v-icon {
  margin-left: 3px;
}

.channel-preview-card-title {
  color: #334155;
  font-size: 13px;
  line-height: 17px;
  font-weight: 700;
}

.channel-preview-card-meta {
  margin-top: 2px;
  color: #87909d;
  font-size: 11px;
  line-height: 14px;
}

.channel-preview-dialog-title {
  padding-bottom: 12px;
}

.channel-preview-dialog-subtitle {
  margin-top: 3px;
  color: #7a828f;
  font-size: 13px;
  line-height: 18px;
  font-weight: 400;
}

.channel-preview-dialog-body {
  padding-top: 8px !important;
}

.channel-preview-dialog-player {
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: 8px;
  background: #000;
  aspect-ratio: 16 / 9;
}

.channel-preview-dialog-video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
}

.channel-preview-dialog-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.18);
  pointer-events: none;
}

.channel-preview-dialog-play {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 92px;
  height: 92px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 2px solid rgba(255, 255, 255, 0.65);
  border-radius: 50%;
  outline: none;
  background: rgba(0, 0, 0, 0.68);
  box-shadow: 0 5px 24px rgba(0, 0, 0, 0.5);
  transform: translate(-50%, -50%);
  cursor: pointer;
}

.channel-preview-dialog-play:hover {
  background: rgba(0, 0, 0, 0.84);
  transform: translate(-50%, -50%) scale(1.05);
}

.channel-preview-dialog-play .v-icon {
  margin-left: 5px;
}

.channel-preview-dialog-error {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  background: rgba(0, 0, 0, 0.82);
  color: #fff;
  text-align: center;
  font-size: 13px;
}

.channel-preview-dialog-url {
  margin-top: 8px;
  overflow-wrap: anywhere;
  color: #8a919d;
  font-size: 11px;
  line-height: 15px;
}

.channel-preview-empty {
  width: 190px;
  min-height: 54px;
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 8px 10px;
  border: 1px dashed #d9dde5;
  border-radius: 7px;
  color: #9aa0aa;
  font-size: 12px;
  line-height: 16px;
  background: #fafbfc;
}

.channels-empty {
  min-height: 120px;
  color: #8a8a8a;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.channels-empty .v-icon {
  opacity: 0.65;
}

.channel-name-line {
  display: flex;
  align-items: flex-start;
}

.settings-props-table th {
  color: grey;
  font-size: 14px;
  font-weight: 500;
}

.settings-layer {
  width: 260px;
  white-space: nowrap;
  font-weight: 600;
  color: #2c3e50;
}

.channel-cell {
  display: grid;
  grid-template-columns: 0 minmax(0, 1fr);
  column-gap: 0;
  align-items: start;
}

.channel-cell--suspended {
  grid-template-columns: 24px minmax(0, 1fr);
  column-gap: 8px;
}

.channel-icon-col {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.channel-pause-icon {
  margin-top: 1px;
}

.channel-text-col {
  min-width: 0;
}

.channel-name {
  font-size: 15px;
  font-weight: 600;
  line-height: 22px;
  color: #2c3e50;
}

.channel-id {
  margin-top: 1px;
  font-size: 12px;
  line-height: 16px;
  color: #8a8a8a;
}

.settings-dialog-title {
  padding-bottom: 4px;
}

.settings-dialog-subtitle {
  margin-bottom: 14px;
  font-size: 15px;
  line-height: 20px;
  font-weight: 700;
  color: #1a75d2;
}

.settings-props-table th {
  height: 34px !important;
  color: grey;
  font-size: 13px;
  line-height: 16px;
  font-weight: 600;
}

.settings-props-table td {
  height: auto !important;
  padding-top: 6px !important;
  padding-bottom: 6px !important;
  vertical-align: middle;
}

.settings-layer {
  width: 260px;
  white-space: nowrap;
  font-size: 13px;
  line-height: 18px;
  font-weight: 700;
  color: #2c3e50;
}

.settings-type {
  width: 80px;
  font-size: 13px;
  color: #2c3e50;
}

.settings-auto {
  width: 90px;
}

.settings-value {
  max-width: 520px;
  font-size: 13px;
  line-height: 18px;
  color: #2c3e50;
  word-break: break-word;
}

.settings-media-link {
  display: flex;
  align-items: center;
  gap: 10px;
  color: inherit;
  text-decoration: none;
}

.settings-media-link:hover .settings-media-url {
  color: var(--v-primary-base);
  text-decoration: underline;
}

.settings-media-box {
  position: relative;
  flex: 0 0 auto;
  width: 92px;
  height: 52px;
  overflow: hidden;
  border-radius: 7px;
  background: #eef1f5;
  border: 1px solid #dfe3ea;
}

.settings-media-img,
.settings-media-video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.settings-media-open {
  position: absolute;
  right: 5px;
  bottom: 5px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(20, 24, 35, 0.58);
  display: flex;
  align-items: center;
  justify-content: center;
}

.settings-media-url {
  min-width: 0;
  max-width: 420px;
  overflow: hidden;
  font-size: 13px;
  line-height: 18px;
  color: #2c3e50;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.settings-auto-value {
  font-weight: 600;
  color: var(--v-primary-base);
}

.move-dialog-title {
  align-items: flex-start;
}

.move-dialog-subtitle {
  margin-top: 4px;
  color: #1a75d2;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
}

.move-dialog-body {
  padding-top: 8px !important;
}

.move-template-selector-row {
  margin-top: 0;
  margin-bottom: 8px;
  align-items: center;
}

.move-template-label {
  margin-bottom: 6px;
  color: #8a8a8a;
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.move-template-card {
  min-height: 40px;
  padding: 8px 12px;
  border: 1px solid #dfe3ea;
  border-radius: 4px;
  background: #f8f9fa;
}

.move-template-card-title {
  color: #2c3e50;
  font-size: 14px;
  line-height: 18px;
  font-weight: 700;
}

.move-template-card-name {
  margin-top: 3px;
  font-size: 13px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.66);
}

.move-template-card-meta {
  margin-top: 2px;
  color: #8a8a8a;
  font-size: 12px;
  line-height: 16px;
}

.move-template-arrow-col {
  display: flex;
  align-items: center;
  justify-content: center;
}

.move-comparison {
  margin-top: 8px;
  border: 1px solid #e0e4ea;
  border-radius: 4px;
  overflow: hidden;
}

.move-comparison-table {
  table-layout: fixed;
}

.move-comparison-table th,
.move-comparison-table td {
  border-right: 1px solid #edf0f4;
}

.move-comparison-table th:last-child,
.move-comparison-table td:last-child {
  border-right: none;
}

.move-group-header {
  height: 38px !important;
  background: #f7f9fc;
  color: #2c3e50 !important;
  font-size: 13px !important;
  font-weight: 700 !important;
}

.move-group-header--current {
  border-left: 3px solid #9aa4b2;
}

.move-group-header--new {
  border-left: 3px solid var(--v-primary-base);
}

.move-arrow-header,
.move-arrow-cell {
  width: 42px;
  min-width: 42px;
  max-width: 42px;
  padding-left: 6px !important;
  padding-right: 6px !important;
  text-align: center !important;
}

.move-layer-cell {
  width: 210px;
  max-width: 210px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.move-value-cell {
  width: auto;
}

.move-row--matched td:nth-child(n + 6) {
  background: #f8fbff;
}

.move-row--new td:nth-child(n + 6) {
  background: #f4f9ff;
}

.move-row--removed td:nth-child(-n + 4) {
  background: #fafafa;
}

.move-empty-value {
  color: #b0b7c3;
}

.move-media-box {
  width: 72px;
  height: 42px;
}

.move-target-empty {
  min-height: 180px;
  margin-top: 8px;
  border: 1px dashed #d5dae2;
  border-radius: 4px;
  color: #8a8a8a;
  display: flex;
  align-items: center;
  justify-content: center;
}


.move-template-option--same-type {
  background: rgba(25, 118, 210, 0.08);
}

.move-template-option--same-type:hover {
  background: rgba(25, 118, 210, 0.14);
}

</style>
